import React, { useEffect, useState } from 'react';
import './pwa.css';

/**
 * Jarvis as an installed app.
 *
 * Gordon: "Lets start creating the desktop app." The decided shape is an
 * INSTALLABLE WEB APP rather than a separate program: the same site, which
 * Edge and Chrome install with its own icon and its own window, and which
 * every deploy updates. There is nothing to download for it and nothing to
 * keep current - the service worker (public/sw.js) takes over the moment a
 * new build is live, and this module is where the page learns that it did.
 *
 * Three things live here, because the pieces that make a site installable
 * are scattered across browser events and have to be caught early:
 *
 *   THE INSTALL PROMPT. Chromium fires `beforeinstallprompt` once, soon after
 *   load, and only on a page that passes its installability checks. It is
 *   caught here at import time - index.js imports this before React mounts -
 *   and kept, so the Install page can offer a button whenever the event has
 *   already come and gone.
 *
 *   THE UPDATE. serviceWorkerRegistration.js announces a new worker taking
 *   over by dispatching UPDATE_EVENT on window. useUpdateAvailable() turns
 *   that into state, and UpdateToast is the small "Jarvis has been updated"
 *   bar that index.js mounts beside App - so App.js is untouched.
 *
 *   WHICH INSTRUCTIONS TO SHOW. When there is no prompt (Safari, Firefox,
 *   an already-installed app), the page explains the OS's own route.
 */

export const UPDATE_EVENT = 'jarvis:sw-updated';

/* ------------------------------------------------------- the install prompt */

let deferredPrompt = null;
let installedNow = false;
const promptListeners = new Set();
const notify = () => promptListeners.forEach((fn) => { try { fn(); } catch { /* a listener must not break the page */ } });

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Hold the browser's own mini-infobar back: the Install page offers the
    // button in context instead.
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    installedNow = true;
    notify();
  });
}

/** Helpers around the stored beforeinstallprompt event. */
export const installPrompt = {
  /** Whether the browser has offered to install, and nobody has yet. */
  available: () => Boolean(deferredPrompt),
  /** Show the browser's install dialog. Resolves to 'accepted' | 'dismissed' | null. */
  async prompt() {
    const e = deferredPrompt;
    if (!e) return null;
    deferredPrompt = null;
    notify();
    try {
      await e.prompt();
      const choice = await e.userChoice;
      const outcome = choice?.outcome || null;
      // Accepted: the browser is installing, and `appinstalled` follows in a
      // moment - but the page should say so now rather than fall back to the
      // manual steps in between. Dismissed: keep the event, the button stays.
      if (outcome === 'accepted') installedNow = true;
      if (outcome === 'dismissed') deferredPrompt = e;
      notify();
      return outcome;
    } catch {
      return null;
    }
  },
  /** True once `appinstalled` fired in this page's lifetime. */
  installedJustNow: () => installedNow,
  subscribe(fn) { promptListeners.add(fn); return () => promptListeners.delete(fn); }
};

/** Is this page already running as the installed app (its own window)? */
export function isStandalone() {
  if (typeof window === 'undefined') return false;
  try {
    return window.matchMedia('(display-mode: standalone)').matches
      || window.matchMedia('(display-mode: window-controls-overlay)').matches
      || window.navigator.standalone === true; // iOS Safari
  } catch {
    return false;
  }
}

/**
 * Enough about the browser to pick the right set of instructions. Not for
 * anything that changes behaviour - only for which paragraph is shown.
 */
export function platformHint(ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '')) {
  const u = String(ua || '');
  const iOS = /iPhone|iPad|iPod/.test(u) || (/Macintosh/.test(u) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1);
  if (iOS) return { os: 'ios', browser: /CriOS|EdgiOS|FxiOS/.test(u) ? 'other' : 'safari' };
  if (/Android/.test(u)) return { os: 'android', browser: /EdgA/.test(u) ? 'edge' : /Chrome/.test(u) ? 'chrome' : 'other' };
  const os = /Windows/.test(u) ? 'windows' : /Macintosh/.test(u) ? 'mac' : /Linux/.test(u) ? 'linux' : 'other';
  let browser = 'other';
  if (/Edg\//.test(u)) browser = 'edge';
  else if (/Firefox\//.test(u)) browser = 'firefox';
  else if (/Chrome\//.test(u)) browser = 'chrome';
  else if (/Safari\//.test(u)) browser = 'safari';
  return { os, browser };
}

/** React: whether the install button can be offered, re-rendered as that changes. */
export function useInstallPrompt() {
  const [, tick] = useState(0);
  useEffect(() => installPrompt.subscribe(() => tick((n) => n + 1)), []);
  return {
    available: installPrompt.available(),
    installedJustNow: installPrompt.installedJustNow(),
    standalone: isStandalone(),
    prompt: installPrompt.prompt
  };
}

/* -------------------------------------------------------------- the update */

let updated = false;
const updateListeners = new Set();
if (typeof window !== 'undefined') {
  window.addEventListener(UPDATE_EVENT, () => {
    updated = true;
    updateListeners.forEach((fn) => { try { fn(true); } catch { /* see above */ } });
  });
}

/**
 * True once a new service worker has taken over this page. The build that
 * is running is then older than the one on the server, and a reload gets the
 * new one. Fires for the fake event the tests dispatch exactly as for the
 * real one, because the page cannot tell the difference and should not try.
 */
export function useUpdateAvailable() {
  const [is, setIs] = useState(updated);
  useEffect(() => {
    updateListeners.add(setIs);
    if (updated) setIs(true);
    return () => updateListeners.delete(setIs);
  }, []);
  return is;
}

/**
 * The toast. Mounted once from index.js, outside App, so it survives every
 * route change and App.js need not know it exists.
 */
export function UpdateToast() {
  const available = useUpdateAvailable();
  const [hidden, setHidden] = useState(false);
  if (!available || hidden) return null;
  return (
    <div className="pwa-toast" role="status" aria-live="polite" data-testid="pwa-update-toast">
      <span className="pwa-toast-dot" aria-hidden="true" />
      <span className="pwa-toast-text">Jarvis has been updated.</span>
      <button type="button" className="pwa-toast-reload" onClick={() => window.location.reload()}>
        Reload
      </button>
      <button type="button" className="pwa-toast-dismiss" aria-label="Not now" onClick={() => setHidden(true)}>
        ×
      </button>
    </div>
  );
}
